import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer, Pencil, Trash2, Plus, Share2, ClipboardList } from "lucide-react";
import { ReelShare, refreshReel } from "../../components/app/ReelShare";
import { useFamilyRole } from "../../hooks/useFamilyRole";
import { toast } from "sonner";
import { PlayerForm } from "../../components/app/PlayerForm";
import { CombineForm } from "../../components/app/CombineForm";
import { HighlightManager, HighlightList, linksOf } from "../../components/app/Highlights";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { useAuth, levelOf } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { api, web, errMsg, avatarSrc, toList } from "../../lib/api";
import { PageHeader, Panel, Loader, ErrorNote, Empty, Avatar, Btn, PillTabs, StatTile, height, fmt, shortDate, firstPosition, secondPosition, PracticeTag, CaptainBadge, StarterTag } from "../../components/app/kit";
import { StatsPanel } from "../../components/app/StatsPanel";
import { CombinePanel } from "../../components/app/CombinePanel";
import { AutoData } from "../../components/app/AutoData";
import { QuizProgress } from "../../components/app/QuizProgress";
import { PhotoPicker } from "../../components/app/PhotoPicker";
import { Archetypes, ArchetypeEditor } from "../../components/app/Archetypes";
import { CoachNotes } from "../../components/app/CoachNotes";
import { DevelopmentPlan } from "../../components/app/DevelopmentPlan";
import { ShotChart } from "../../components/app/ShotChart";
import { TradingCard } from "../../components/app/TradingCard";
import { EditionAlert } from "../../components/app/EditionAlert";
import { BenchmarkRank } from "../../components/app/BenchmarkRank";
import { useLeaders } from "../../hooks/useLeaders";
import { useCWeek, cWeekFor } from "../../hooks/useCWeek";
import { WinnerBanner } from "../../components/app/WinnerBanner";

const Recruiting = ({ base }) => {
  const interest = useApi(`${base}/interest`);
  const notes = useApi(`${base}/recruiter-notes`);
  const d = interest.data ?? {};
  const colleges = toList(d.colleges);
  const timeline = toList(d.timeline);
  const list = toList(notes.data, ["notes"]);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel>
        <div className="mb-4 flex items-end justify-between">
          <h3 className="font-display text-xl font-bold uppercase text-white">College interest</h3>
          <span data-testid="interest-count" className="font-mono-gd text-4xl font-bold text-[#39FF14]">{d.count ?? 0}</span>
        </div>
        {interest.loading ? <Loader /> : interest.error ? <ErrorNote error={interest.error} /> : (
          <>
            {timeline.length > 0 && (
              <div className="mb-4 h-40">
                <ResponsiveContainer>
                  <AreaChart data={timeline}>
                    <XAxis dataKey="week" stroke="#94A3B8" fontSize={10} tickLine={false} />
                    <YAxis stroke="#94A3B8" fontSize={10} width={24} allowDecimals={false} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 12 }} />
                    <Area dataKey="count" stroke="#39FF14" fill="rgba(57,255,20,0.2)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            {colleges.length ? (
              <ul className="space-y-2">
                {colleges.map((c, i) => (
                  <li key={i} className="flex justify-between rounded-xl bg-[#162032] px-4 py-3 text-sm">
                    <span className="text-white">{c.recruiter_org ?? "—"} <span className="text-slate-400">· {c.recruiter_name}</span></span>
                    <span className="font-mono-gd text-xs text-slate-400">{shortDate(c.since)}</span>
                  </li>
                ))}
              </ul>
            ) : <Empty title="No recruiters yet" />}
          </>
        )}
      </Panel>
      <Panel>
        <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Recruiter notes</h3>
        {notes.loading ? <Loader /> : notes.error ? <ErrorNote error={notes.error} /> : list.length ? (
          <ul className="space-y-3">
            {list.map((n, i) => (
              <li key={n.id ?? i} data-testid="recruiter-note-item" className="rounded-xl border border-[#1E293B] p-4">
                <p className="text-sm text-slate-100">{n.body}</p>
                <p className="mt-2 font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">{n.recruiter_org} · {n.recruiter_name} · {shortDate(n.created_at)}</p>
              </li>
            ))}
          </ul>
        ) : <Empty title="No notes shared" />}
      </Panel>
    </div>
  );
};

const Development = ({ base, tid, pid, canEdit }) => {
  const att = useApi(`/teams/${tid}/attendance?player_id=${pid}`);
  return (
    <div className="space-y-6">
      <DevelopmentPlan tid={tid} pid={pid} canEdit={canEdit} />
      <Panel>
        <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Attendance</h3>
        {att.loading ? <Loader /> : att.error ? <ErrorNote error={att.error} /> : <AutoData value={att.data} testid="dev-attendance" />}
      </Panel>
    </div>
  );
};

const Combine = ({ base, tid, pid, canEdit, teammate, player }) => {
  const combine = useApi(teammate ? `/teams/${tid}/teammates/${pid}/combine` : `${base}/combine`, teammate ? web : api);
  const [adding, setAdding] = useState(false);
  const del = async (s) => {
    if (!window.confirm(`Delete the combine session from ${shortDate(s.date)}?`)) return;
    try { await api.delete(`/teams/${tid}/combine/${s.id}`); toast.success("Session deleted"); combine.reload(); refreshReel(tid, pid); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      {canEdit && (
        <div className="no-print mb-5 flex justify-end">
          <Btn data-testid="add-combine-button" onClick={() => setAdding(true)}><Plus className="h-4 w-4" />Add combine session</Btn>
          <CombineForm open={adding} onClose={() => setAdding(false)} tid={tid} pid={pid} onSaved={() => { combine.reload(); refreshReel(tid, pid); }} />
        </div>
      )}
      {teammate && combine.data?.cached_at && (
        <p data-testid="teammate-combine-asof" className="mb-4 font-mono-gd text-xs uppercase tracking-wider text-slate-400">Results as of {shortDate(combine.data.cached_at)}</p>
      )}
      {combine.loading ? <Loader /> : combine.error ? <ErrorNote error={combine.error} /> : (
        <CombinePanel player={player} latest={combine.data?.latest} bests={combine.data?.personal_bests} comparison={combine.data?.comparison} sessions={toList(combine.data?.sessions)}
          onDelete={canEdit ? del : undefined} emptyText={canEdit ? "Add a session to log measurements and testing results." : undefined} />
      )}
    </div>
  );
};

/** Coaches mark a starter right from the profile; starters lead their 1st position on the depth chart. */
const StarterToggle = ({ tid, p, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const flip = async () => {
    setBusy(true);
    try {
      await web.put(`/teams/${tid}/players/${p.id}/starter`, { starter: !p.starter });
      toast.success(p.starter ? `${p.name} is no longer a starter` : `${p.name} is now a starter`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid="player-starter-toggle" aria-pressed={!!p.starter} disabled={busy} onClick={flip}
      className={`rounded-full border px-3 py-1 transition-colors disabled:opacity-50 ${p.starter ? "border-[#00E5FF] bg-[#00E5FF]/15 text-[#7DF3FF]" : "border-dashed border-[#1E293B] text-slate-300 hover:border-[#00E5FF]/60 hover:text-white"}`}>
      {p.starter ? "Starter ✓" : "+ Mark starter"}
    </button>
  );
};

export default function PlayerDetail() {
  const { playerId } = useParams();
  const { membership, canEdit } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const openCard = search.get("card") === "open";
  const tid = membership?.team_id;
  const base = `/teams/${tid}/players/${playerId}`;
  const [tab, setTab] = useState("Stats");
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [typing, setTyping] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });
  const qs = range.from && range.to ? `?start=${range.from}&end=${range.to}` : "";
  // Players and parents viewing someone else's profile get the read-only teammate view (no GPA, recruiting, development or notes).
  const teammate = !canEdit && membership?.player_id !== playerId;
  const client = teammate ? web : api;
  const viewBase = teammate ? `/teams/${tid}/teammates/${playerId}` : base;
  const player = useApi(tid && viewBase, client);
  const stats = useApi(tid && tab === "Stats" && `${viewBase}/stats${qs}`, client);
  const cardStats = useApi(tid && teammate && `${viewBase}/stats`, web);
  const headCombine = useApi(tid && `${viewBase}/combine`, client);
  const badges = useLeaders(tid);
  const cw = useCWeek(tid);
  const p = player.data?.player ?? player.data ?? {};
  useEffect(() => { if (canEdit && tid) refreshReel(tid, playerId); }, [canEdit, tid, playerId]);
  const onHighlights = () => { player.reload(); refreshReel(tid, playerId); };
  const family = useFamilyRole();
  const ownProfile = family.isPlayer && membership?.player_id === playerId;

  if (player.loading && !player.data) return <Loader />;
  if (player.error) return <ErrorNote error={player.error} />;
  const season = stats.data?.season ?? stats.data;
  const remove = async () => {
    if (!window.confirm(`Remove ${p.name} from the roster? Their stats stay in past box scores.`)) return;
    try {
      await api.delete(base);
      toast.success(`${p.name} removed from the roster`);
      navigate("/coach/roster");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <Link to="/coach/roster" data-testid="back-to-roster" className="no-print mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#39FF14]"><ArrowLeft className="h-4 w-4" />Roster</Link>
      <div className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-center">
        {canEdit || ownProfile
          ? <PhotoPicker name={p.name ?? ""} src={avatarSrc(tid, p)} path={`/teams/${tid}/players/${playerId}/photo`} onDone={player.reload} testid="player-photo-button" />
          : <Avatar name={p.name ?? ""} src={avatarSrc(tid, p)} size="h-24 w-24" />}
        <div className="flex-1">
          <PageHeader eyebrow={`#${p.jersey ?? "–"} · ${levelOf(p) === "jv" ? "JV" : "Varsity"} · ${firstPosition(p)} · Class of ${p.grad_year ?? "—"}`} title={p.name ?? "Player"}>
            <Btn variant="ghost" data-testid="print-player-button" onClick={() => window.print()}><Printer className="h-4 w-4" />Print report</Btn>
            {canEdit && <Btn data-testid="player-enter-stats-button" onClick={() => navigate(`/coach/enter-stats?player=${playerId}`)}><ClipboardList className="h-4 w-4" />Enter stats</Btn>}
            {canEdit && <Btn data-testid="share-reel-button" onClick={() => setSharing(true)}><Share2 className="h-4 w-4" />Share highlights</Btn>}
            {canEdit && <Btn variant="ghost" data-testid="edit-player-button" onClick={() => setEditing(true)}><Pencil className="h-4 w-4" />Edit</Btn>}
            {canEdit && <Btn variant="danger" data-testid="remove-player-button" onClick={remove}><Trash2 className="h-4 w-4" />Remove</Btn>}
          </PageHeader>
          <div data-testid="player-positions" className="-mt-3 mb-4 flex flex-wrap items-center gap-2 font-mono-gd text-xs uppercase tracking-wider text-slate-300">
            <span data-testid="player-position-1st" className="rounded-full border border-[#39FF14]/40 px-3 py-1 text-white">1st position <b className="text-[#39FF14]">{firstPosition(p)}</b></span>
            <span data-testid="player-position-2nd" className="rounded-full border border-[#1E293B] px-3 py-1">2nd position <b className="text-white">{secondPosition(p) ?? "—"}</b></span>
            {p.captain && <span data-testid="player-captain-tag" className="inline-flex items-center gap-1.5 rounded-full border border-[#39FF14]/40 px-2.5 py-0.5 text-[#39FF14]"><CaptainBadge className="h-4 w-4 text-[9px]" />Team captain</span>}
            {p.practice_player && <PracticeTag testid="player-practice-tag" />}
            {canEdit ? <StarterToggle tid={tid} p={p} onSaved={player.reload} /> : p.starter && <StarterTag testid="player-starter-tag" />}
          </div>
          <BenchmarkRank className="-mt-2" player={p} bests={headCombine.data?.personal_bests} />
          <WinnerBanner award={Object.values(cw.current).find((a) => a?.player_id === playerId)} count={cw.counts[playerId] ?? 0} />
          {canEdit && <PlayerForm open={editing} onClose={() => setEditing(false)} teamId={tid} player={p} onSaved={onHighlights} />}
          {canEdit && <ReelShare open={sharing} onClose={() => setSharing(false)} tid={tid} pid={playerId} name={p.name ?? "this player"} />}
        </div>
      </div>
      <div className="mb-6 flex items-center gap-4">
        <TradingCard p={{ ...p, leader_badges: badges[playerId], c_week: cWeekFor(cw, playerId) }} teamName={membership?.team_name} photo={avatarSrc(tid, p)} {...(teammate ? { season: cardStats.data?.season, perGame: cardStats.data?.per_game } : { statsPath: `${base}/stats` })} autoOpen={openCard} onClose={() => { if (openCard) setSearch({}, { replace: true }); }} />
        {canEdit && <EditionAlert tid={tid} pid={playerId} onOpenCard={() => setSearch({ card: "open" }, { replace: true })} />}
        <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Height" value={height(p.height_in)} /><StatTile label="Weight" value={p.weight_lb ? `${p.weight_lb} lb` : "—"} />
          {teammate ? <StatTile label="Class" value={p.grad_year ?? "—"} /> : <StatTile label="GPA" value={fmt(p.gpa)} />}<StatTile label="Status" value={p.status ?? "—"} />
        </div>
      </div>
      <Archetypes p={p} canEdit={canEdit} onEdit={() => setTyping(true)} />
      {canEdit && <ArchetypeEditor open={typing} onClose={() => setTyping(false)} tid={tid} p={p} onSaved={player.reload} />}
      {(canEdit || ownProfile) && <div className="no-print"><QuizProgress tid={tid} pid={playerId} /></div>}
      <PillTabs tabs={teammate ? ["Stats", "Shot Chart", "Combine", "Highlights"] : ["Stats", "Shot Chart", "Combine", "Highlights", "Recruiting", "Development", ...(canEdit ? ["Notes"] : [])]} value={tab} onChange={setTab} />
      {tab === "Stats" && (
        <>
          <div className="no-print mb-5 flex flex-wrap items-center gap-2 text-sm">
            <span className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">Date range</span>
            <input type="date" data-testid="stats-from-input" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="rounded-lg border border-[#1E293B] bg-[#0F172A] px-3 py-1.5 text-white [color-scheme:dark]" />
            <input type="date" data-testid="stats-to-input" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="rounded-lg border border-[#1E293B] bg-[#0F172A] px-3 py-1.5 text-white [color-scheme:dark]" />
            {qs && <button data-testid="stats-clear-range" onClick={() => setRange({ from: "", to: "" })} className="text-slate-400 underline">Clear</button>}
          </div>
          {stats.loading ? <Loader /> : stats.error ? <ErrorNote error={stats.error} /> : <StatsPanel season={season} perGame={stats.data?.per_game ?? season?.per_game} zonePct={stats.data?.zone_pct} />}
        </>
      )}
      {tab === "Shot Chart" && <ShotChart tid={tid} pid={playerId} canEdit={canEdit} />}
      {tab === "Combine" && <Combine base={base} tid={tid} pid={playerId} canEdit={canEdit} teammate={teammate} player={p} />}
      {tab === "Highlights" && (canEdit ? <HighlightManager tid={tid} pid={playerId} player={p} onChange={onHighlights} />
        : ownProfile ? <HighlightManager playerMode tid={tid} pid={playerId} player={p} onChange={player.reload} /> : <HighlightList links={linksOf(p)} />)}
      {tab === "Recruiting" && !teammate && <Recruiting base={base} />}
      {tab === "Development" && !teammate && <Development base={base} tid={tid} pid={playerId} canEdit={canEdit} />}
      {tab === "Notes" && canEdit && <CoachNotes tid={tid} pid={playerId} />}
    </div>
  );
}
